import type { Ref } from "react";

import clsx from "clsx";
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ArrowDownIcon, CheckIcon, GlobeIcon } from "../Icons";
import { Typo } from "../Typo";

export type LanguageFilterChipOption = {
  label: string;
  value: string;
};

export type LanguageFilterChipTranslations = {
  label: string;
  multipleSelected?: (count: number) => string;
};

export type LanguageFilterChipVariant = "chip" | "pill" | "trigger";

type LabelContext = {
  options: LanguageFilterChipOption[];
  selectedValues: string[];
  translations?: LanguageFilterChipTranslations;
};

type VariantStyle = {
  trigger: string;
  typoType: "bodyXS" | "labelSM";
  typoColor?: string;
  arrow: string;
  prefixIcon: boolean;
  dropdown: string;
  itemRadius: string;
  spacerWhenUnselected: boolean;
  buildLabel: (ctx: LabelContext) => string;
};

const labelOf = (
  options: LanguageFilterChipOption[],
  value: string | undefined,
) => (value ? (options.find((o) => o.value === value)?.label ?? value) : "");

const buildSelectedLabel = ({ options, selectedValues }: LabelContext) =>
  labelOf(options, selectedValues[0]);

const buildFilterLabel = ({
  options,
  selectedValues,
  translations,
}: LabelContext) => {
  if (!translations) return "";
  if (selectedValues.length === 0) return translations.label;
  if (selectedValues.length === 1) {
    return `${translations.label}: ${labelOf(options, selectedValues[0])}`;
  }
  return `${translations.label}: ${translations.multipleSelected?.(selectedValues.length) ?? ""}`;
};

const VARIANT_STYLES: Record<LanguageFilterChipVariant, VariantStyle> = {
  chip: {
    trigger:
      "flex items-center gap-1 rounded-full border border-stroke-primary py-1 px-2 text-text-primary",
    typoType: "labelSM",
    arrow: "[&_path]:stroke-icon-primary",
    prefixIcon: false,
    dropdown: "flex flex-col items-stretch gap-1 p-4",
    itemRadius: "rounded-lg",
    spacerWhenUnselected: true,
    buildLabel: buildFilterLabel,
  },
  pill: {
    trigger:
      "text-text-brand flex items-center gap-1 rounded-full bg-surface-primary px-2 py-1",
    typoType: "bodyXS",
    typoColor: "text-text-brand-default",
    arrow: "[&_path]:stroke-text-brand-default",
    prefixIcon: true,
    dropdown: "p-2",
    itemRadius: "rounded-md",
    spacerWhenUnselected: false,
    buildLabel: buildSelectedLabel,
  },
  trigger: {
    trigger: "flex items-center gap-1 text-text-brand",
    typoType: "bodyXS",
    typoColor: "text-text-brand-default",
    arrow: "[&_path]:stroke-text-brand-default",
    prefixIcon: false,
    dropdown: "p-2",
    itemRadius: "rounded-md",
    spacerWhenUnselected: false,
    buildLabel: buildSelectedLabel,
  },
};

type LanguageFilterChipProps = {
  options: LanguageFilterChipOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  mode?: "single" | "multi";
  variant?: LanguageFilterChipVariant;
  translations?: LanguageFilterChipTranslations;
  dropUp?: boolean;
  align?: "left" | "right";
  className?: string;
  "data-testid"?: string;
  /** Use fixed positioning with a portal to escape scroll containers */
  modal?: boolean;
};

export const LanguageFilterChip = ({
  options,
  selectedValues,
  onChange,
  mode = "multi",
  variant = "chip",
  translations,
  dropUp = false,
  align = "left",
  className,
  "data-testid": testId,
  modal = false,
}: LanguageFilterChipProps) => {
  const VIEWPORT_MARGIN = 8;
  const DROPDOWN_GAP = 4;
  const DEFAULT_DROPDOWN_MIN_WIDTH = 200;
  const DEFAULT_DROPDOWN_MAX_HEIGHT = 200;

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    maxHeight: DEFAULT_DROPDOWN_MAX_HEIGHT,
  });
  // Track when position has been calculated to avoid flash at (0,0) on first render
  const [positionReady, setPositionReady] = useState(false);
  const style = VARIANT_STYLES[variant];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer =
        containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown =
        !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset position ready state when dropdown closes to prevent flash at old position on reopen
  useEffect(() => {
    if (!isOpen) {
      setPositionReady(false);
    }
  }, [isOpen]);

  // Calculate dropdown position for portal mode
  useLayoutEffect(() => {
    if (!modal || !isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownEl = dropdownRef.current;
      const measuredWidth = dropdownEl?.offsetWidth ?? 0;
      const measuredHeight = dropdownEl?.scrollHeight ?? 0;
      const dropdownWidth = Math.max(
        DEFAULT_DROPDOWN_MIN_WIDTH,
        measuredWidth || DEFAULT_DROPDOWN_MIN_WIDTH,
      );
      const dropdownHeight = Math.min(
        DEFAULT_DROPDOWN_MAX_HEIGHT,
        measuredHeight || DEFAULT_DROPDOWN_MAX_HEIGHT,
      );

      const spaceBelow =
        viewportHeight - rect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const shouldDropUp =
        dropUp || (spaceBelow < dropdownHeight && spaceAbove > spaceBelow);

      const preferredLeft =
        align === "left" ? rect.left : rect.right - dropdownWidth;
      const maxLeft = viewportWidth - VIEWPORT_MARGIN - dropdownWidth;
      const clampedLeft = Math.max(
        VIEWPORT_MARGIN,
        Math.min(preferredLeft, maxLeft),
      );

      const availableHeight = shouldDropUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(
        80,
        Math.min(DEFAULT_DROPDOWN_MAX_HEIGHT, availableHeight),
      );
      const renderedHeight = Math.min(dropdownHeight, maxHeight);

      const preferredTop = shouldDropUp
        ? rect.top - DROPDOWN_GAP - renderedHeight
        : rect.bottom + DROPDOWN_GAP;
      const maxTop = viewportHeight - VIEWPORT_MARGIN - maxHeight;
      const clampedTop = Math.max(
        VIEWPORT_MARGIN,
        Math.min(preferredTop, maxTop),
      );

      setDropdownPosition({
        top: clampedTop,
        left: clampedLeft,
        maxHeight,
      });
    };

    // Find all scrollable parents to attach scroll listeners
    const getScrollableParents = (element: HTMLElement): HTMLElement[] => {
      const scrollableParents: HTMLElement[] = [];
      let parent = element.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowX === "auto" ||
          overflowX === "scroll"
        ) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }
      return scrollableParents;
    };

    const scrollableParents = getScrollableParents(triggerRef.current);

    // Close dropdown when scrolling inside a scrollable parent (e.g. modal)
    // to prevent the dropdown from floating outside modal boundaries
    const closeOnScroll = () => setIsOpen(false);

    // Reset position ready state when dropdown opens
    setPositionReady(false);

    updatePosition();
    const rafId = window.requestAnimationFrame(() => {
      updatePosition();
      setPositionReady(true);
    });

    // Close dropdown when scrolling inside parent containers
    for (const parent of scrollableParents) {
      parent.addEventListener("scroll", closeOnScroll);
    }
    // Update position on resize
    window.addEventListener("resize", updatePosition);
    return () => {
      window.cancelAnimationFrame(rafId);
      for (const parent of scrollableParents) {
        parent.removeEventListener("scroll", closeOnScroll);
      }
      window.removeEventListener("resize", updatePosition);
    };
  }, [modal, isOpen, dropUp, align]);

  const triggerLabel = style.buildLabel({
    options,
    selectedValues,
    translations,
  });

  const handleSelect = (value: string) => {
    if (mode === "single") {
      onChange([value]);
      setIsOpen(false);
      return;
    }
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <div
      className={clsx("relative inline-block", className)}
      ref={containerRef}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        data-testid={testId}
        className={style.trigger}
      >
        {style.prefixIcon && <GlobeIcon className="h-3 w-3" />}
        <Typo type={style.typoType} textColor={style.typoColor}>
          {triggerLabel}
        </Typo>
        <ArrowDownIcon className={clsx("h-2 w-2", style.arrow)} />
      </button>
      {isOpen && (
        <DropdownContent
          ref={dropdownRef}
          modal={modal}
          dropdownPosition={dropdownPosition}
          positionReady={positionReady}
          dropUp={dropUp}
          align={align}
          style={style}
          testId={testId}
          options={options}
          selectedValues={selectedValues}
          handleSelect={handleSelect}
        />
      )}
    </div>
  );
};

type DropdownContentProps = {
  modal: boolean;
  dropdownPosition: { top: number; left: number; maxHeight: number };
  positionReady: boolean;
  dropUp: boolean;
  align: "left" | "right";
  style: VariantStyle;
  testId?: string;
  options: LanguageFilterChipOption[];
  selectedValues: string[];
  handleSelect: (value: string) => void;
};

const DropdownContent = forwardRef(
  (
    {
      modal,
      dropdownPosition,
      positionReady,
      dropUp,
      align,
      style,
      testId,
      options,
      selectedValues,
      handleSelect,
    }: DropdownContentProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const dropdownClasses = modal
      ? clsx(
          "fixed z-floating max-h-[200px] min-w-[200px] overflow-y-auto rounded-lg border border-stroke-primary bg-surface-primary shadow-dropdown",
          style.dropdown,
        )
      : clsx(
          "absolute z-50 max-h-[200px] min-w-[200px] overflow-y-auto rounded-lg border border-stroke-primary bg-surface-primary shadow-dropdown",
          style.dropdown,
          dropUp ? "bottom-full mb-1" : "top-full mt-1",
          align === "left" ? "left-0" : "right-0",
        );

    const dropdownStyle = modal
      ? {
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          maxHeight: dropdownPosition.maxHeight,
          // Hide dropdown until position is calculated to avoid flash at (0,0)
          visibility: positionReady
            ? ("visible" as const)
            : ("hidden" as const),
        }
      : undefined;

    const content = (
      <div
        ref={ref}
        className={dropdownClasses}
        style={dropdownStyle}
        data-testid={testId ? `${testId}-dropdown` : undefined}
      >
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={clsx(
                "flex w-full items-center justify-between p-2 hover:bg-surface-tonal-default",
                style.itemRadius,
                isSelected && "bg-state-selected",
              )}
            >
              <Typo
                type="bodySM"
                textColor={isSelected ? "text-text-selected" : undefined}
              >
                {option.label}
              </Typo>
              {isSelected ? (
                <CheckIcon className="h-4 w-4 [&_path]:stroke-stroke-selected" />
              ) : (
                style.spacerWhenUnselected && <span className="h-4 w-4" />
              )}
            </button>
          );
        })}
      </div>
    );

    if (modal) {
      return createPortal(content, document.body);
    }

    return content;
  },
);

DropdownContent.displayName = "DropdownContent";
